import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Wordmark, Rule } from "@/components/editorial";
import {
  faqSchema,
  breadcrumbSchema,
  organizationSchema,
} from "@/lib/seo/schemas";

const FAQS = [
  {
    q: "Qu'est-ce que le batch cooking ?",
    a: "Le batch cooking est une méthode de cuisine consistant à préparer plusieurs repas en une seule session hebdomadaire — généralement le dimanche — pour gagner du temps en semaine. On cuisine les bases (céréales, légumes rôtis, protéines) une fois, puis on les assemble différemment chaque soir.",
  },
  {
    q: "Comment Batch Cooking génère-t-il mon menu ?",
    a: "Notre IA analyse vos préférences (régime alimentaire, allergies, équipement disponible, temps de préparation, taille du foyer) et compose 7 dîners équilibrés. Elle optimise les bases communes pour minimiser le gaspillage et le temps de cuisson total.",
  },
  {
    q: "Est-ce que Batch Cooking est gratuit ?",
    a: "Oui. La formule L'Essentiel est gratuite à vie : elle comprend un menu hebdomadaire pré-composé, la liste de courses et le mode cuisine pas-à-pas. La formule Le Cahier (7,90 €/mois ou 69 €/an) débloque les menus IA entièrement personnalisés, les substitutions illimitées, les thèmes hebdomadaires et le partage foyer.",
  },
  {
    q: "Puis-je indiquer mes allergies et restrictions alimentaires ?",
    a: "Oui. Lors de l'onboarding, vous déclarez vos allergies (gluten, lactose, fruits à coque, etc.), votre régime (végétarien, végan, pescatarien…) et vos intolérances. L'IA en tient compte lors de chaque génération de menu. Toutefois, nous recommandons de toujours vérifier la composition de chaque recette — cf. nos mentions légales.",
  },
  {
    q: "L'application fonctionne-t-elle hors ligne ?",
    a: "En mode premium (Le Cahier), les menus déjà générés et les listes de courses sont accessibles hors ligne. La génération de nouveaux menus et les substitutions IA nécessitent une connexion internet.",
  },
  {
    q: "Batch Cooking est-il disponible sur mobile ?",
    a: "Oui. Batch Cooking est disponible sur iPhone (App Store) et Android (Google Play). L'application web fonctionne également sur tous les navigateurs modernes.",
  },
  {
    q: "Puis-je partager mon menu avec ma famille ?",
    a: "Oui. La formule Le Cahier inclut le partage foyer pour jusqu'à 4 membres : votre liste de courses est partagée et synchronisée en temps réel. Vous pouvez aussi partager un lien de lecture seule de n'importe quel menu, même avec des personnes qui n'ont pas l'application.",
  },
  {
    q: "Comment annuler mon abonnement ?",
    a: "Vous pouvez annuler à tout moment, sans frais ni pénalité. Sur iOS : App Store → votre compte → Abonnements. Sur Android : Play Store → Abonnements. Il n'y a aucun engagement de durée.",
  },
  {
    q: "Combien de temps faut-il pour un batch cooking complet ?",
    a: "En moyenne 2 h 30 de cuisine le dimanche pour préparer les bases de 7 dîners. L'application optimise l'ordre des tâches (ce qui cuit en premier, ce qu'on prépare en parallèle) pour minimiser ce temps.",
  },
  {
    q: "Mes données personnelles sont-elles protégées ?",
    a: "Oui. Vos données sont hébergées en Europe (serveurs Frankfurt) et ne sont jamais revendues à des tiers. Consultez notre politique de confidentialité pour plus de détails.",
  },
] as const;

export function FaqPage() {
  return (
    <div className="min-h-full bg-background font-sans text-foreground">
      <Seo
        title="Questions fréquentes"
        description="Toutes les réponses à vos questions sur Batch Cooking : fonctionnement, tarifs, allergies, partage de menu, disponibilité sur mobile et plus."
        canonicalPath="/faq"
        jsonLd={[
          faqSchema,
          organizationSchema,
          breadcrumbSchema([
            { name: "Accueil", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
        ]}
      />

      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-hair bg-background/95 px-5 py-5 backdrop-blur lg:px-[72px]">
        <Wordmark size="md" />
        <Link
          to="/"
          className="flex items-center gap-1.5 text-[13px] text-ink-70 hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Accueil
        </Link>
      </header>

      <main className="mx-auto max-w-[760px] px-5 pb-24 pt-16 lg:px-8">
        <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-accent">
          FAQ
        </p>
        <h1 className="mb-4 font-serif text-[38px] font-normal leading-tight tracking-[-0.01em] text-foreground lg:text-[48px]">
          Questions <span className="italic">fréquentes</span>.
        </h1>
        <p className="mb-14 max-w-[560px] font-serif text-xl font-light leading-relaxed text-ink-70">
          Tout ce que vous voulez savoir sur Batch Cooking, le batch cooking en
          général, et notre application.
        </p>

        <Rule className="mb-14" />

        <div className="space-y-0">
          {FAQS.map(({ q, a }, i) => (
            <details
              key={q}
              className="group border-b border-hair py-6 first:border-t"
              open={i === 0}
            >
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
                <h2 className="font-serif text-[19px] font-normal leading-snug text-foreground group-open:text-accent">
                  {q}
                </h2>
                <span
                  aria-hidden
                  className="mt-1 shrink-0 text-[18px] font-light text-ink-40 transition-transform group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <p className="mt-4 pr-8 text-[14.5px] leading-relaxed text-ink-70">
                {a}
              </p>
            </details>
          ))}
        </div>

        <div className="mt-16 border-t border-hair pt-10 text-center">
          <p className="mb-4 font-serif text-xl italic text-ink-70">
            Vous n'avez pas trouvé votre réponse ?
          </p>
          <a
            href="mailto:contact@batchcooking.fr"
            className="border-b border-foreground pb-0.5 text-[13px] uppercase tracking-[0.08em] text-foreground hover:border-accent hover:text-accent"
          >
            Contactez-nous
          </a>
        </div>
      </main>
    </div>
  );
}
